/**
 * Componente de formulário de agendamento.
 */

import type { BookingFormData, CustomField, PublicProvider } from "../types";
import {
  isBookingContactComplete,
  isValidBookingEmail,
} from "../utils/bookingValidation";
import {
  FiCalendar,
  FiCheckCircle,
  FiFileText,
  FiMail,
  FiMessageSquare,
  FiPhone,
  FiShield,
  FiUser,
} from "react-icons/fi";
import type { CustomerIdentityField } from "../utils/bookingDraft";
import { CustomFieldsForm } from "./CustomFieldsForm";
import { Button, Input, Textarea } from "../../../components/ui";

interface BookingFormProps {
  formData: BookingFormData;
  onFormDataChange: (data: Partial<BookingFormData>) => void;
  customFields: CustomField[];
  customValues: Record<string, any>;
  onCustomValuesChange: (values: Record<string, any>) => void;
  selectedTime: string;
  date: string;
  selectedProviderId: string;
  providers: PublicProvider[];
  hasSelectedService: boolean;
  loading: boolean;
  identityLocked?: boolean;
  /**
   * MBR-14: campos que vieram da conta. Só eles ficam travados; o que a conta
   * não tem (o WhatsApp de quem foi cadastrado no balcão) segue editável.
   */
  lockedFields?: readonly CustomerIdentityField[];
  isLoggedCustomer?: boolean;
  compact?: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

const MISSING_FIELD_LABELS: Record<CustomerIdentityField, string> = {
  name: "nome",
  email: "e-mail",
  whatsapp: "WhatsApp",
};

export function BookingForm({
  formData,
  onFormDataChange,
  customFields,
  customValues,
  onCustomValuesChange,
  selectedTime,
  date,
  selectedProviderId,
  providers,
  hasSelectedService,
  loading,
  identityLocked = false,
  lockedFields,
  isLoggedCustomer = false,
  compact = false,
  onSubmit,
}: BookingFormProps) {
  const emailIsInvalid =
    Boolean(String(formData.email || "").trim()) && !isValidBookingEmail(formData.email);
  const contactIsComplete = isBookingContactComplete(formData);
  const isLocked = (field: CustomerIdentityField) =>
    identityLocked || Boolean(lockedFields?.includes(field));
  const missingAccountFields =
    isLoggedCustomer && lockedFields
      ? (Object.keys(MISSING_FIELD_LABELS) as CustomerIdentityField[]).filter(
          (field) => !isLocked(field)
        )
      : [];
  const loggedCustomerNotice =
    missingAccountFields.length > 0
      ? `Dados preenchidos pela sua conta. Falta só: ${missingAccountFields
          .map((field) => MISSING_FIELD_LABELS[field])
          .join(", ")}.`
      : "Dados preenchidos automaticamente pela sua conta de cliente.";

  if (compact) {
    const disabledSubmit =
      !hasSelectedService || !selectedTime || !contactIsComplete || loading;

    return (
      <section id="booking-form" className="animate-fade-in">
        <h2 className="text-3xl font-semibold text-[#1f2e52]">Seus dados</h2>

        <form onSubmit={onSubmit} className="mt-3 space-y-3">
          {isLoggedCustomer && (
            <div className="rounded-xl border border-primary-200 bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-800">
              {loggedCustomerNotice}
            </div>
          )}

          <label className="block">
            <span className="mb-1 flex items-center gap-1 text-sm font-medium text-slate-700">
              <span>Nome completo</span>
              <span aria-hidden="true" className="text-rose-500">
                *
              </span>
            </span>
            <input
              value={formData.name}
              onChange={(e) => onFormDataChange({ name: e.target.value })}
              disabled={isLocked("name")}
              required
              placeholder="Digite seu nome"
              className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-800 outline-none transition focus:border-primary-300 focus:ring-4 focus:ring-primary-100"
            />
          </label>

          <label className="block">
            <span className="mb-1 flex items-center gap-1 text-sm font-medium text-slate-700">
              <span>WhatsApp</span>
              <span aria-hidden="true" className="text-rose-500">
                *
              </span>
            </span>
            <input
              value={formData.whatsapp}
              onChange={(e) => {
                const numbers = e.target.value.replace(/\D/g, "");
                let formatted = numbers;
                if (numbers.length >= 11) {
                  formatted = numbers.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
                } else if (numbers.length >= 7) {
                  formatted = numbers.replace(/(\d{2})(\d{5})(\d*)/, "($1) $2-$3");
                } else if (numbers.length >= 3) {
                  formatted = numbers.replace(/(\d{2})(\d*)/, "($1) $2");
                }
                onFormDataChange({ whatsapp: formatted });
              }}
              disabled={isLocked("whatsapp")}
              required
              placeholder="(85) 99999-9999"
              maxLength={15}
              className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-800 outline-none transition focus:border-primary-300 focus:ring-4 focus:ring-primary-100"
            />
          </label>

          <label className="block">
            <span className="mb-1 flex items-center gap-1 text-sm font-medium text-slate-700">
              <span>E-mail para confirmação</span>
              <span aria-hidden="true" className="text-rose-500">
                *
              </span>
            </span>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => onFormDataChange({ email: e.target.value })}
              disabled={isLocked("email")}
              required
              aria-invalid={emailIsInvalid}
              aria-describedby={emailIsInvalid ? "booking-email-error" : undefined}
              placeholder="seu@email.com"
              className={`h-11 w-full rounded-xl border px-3 text-sm text-slate-800 outline-none transition focus:ring-4 ${
                emailIsInvalid
                  ? "border-rose-300 focus:border-rose-500 focus:ring-rose-100"
                  : "border-slate-200 focus:border-primary-300 focus:ring-primary-100"
              }`}
            />
            {emailIsInvalid ? (
              <span
                id="booking-email-error"
                className="mt-1 block text-xs font-medium text-rose-600"
              >
                Informe um e-mail válido.
              </span>
            ) : (
              <span className="mt-1 block text-xs font-medium text-slate-500">
                Usado para enviar a confirmação do agendamento.
              </span>
            )}
          </label>

          {customFields.length > 0 && (
            <CustomFieldsForm
              fields={customFields}
              values={customValues}
              onChange={onCustomValuesChange}
            />
          )}

          <button
            type="submit"
            disabled={disabledSubmit}
            className="mt-2 inline-flex min-h-[3.5rem] w-full items-center justify-center gap-2.5 rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700 px-5 py-3 text-base font-semibold leading-none text-white shadow-lg shadow-primary-600/25 transition hover:from-primary-hover hover:to-primary-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 disabled:cursor-not-allowed disabled:opacity-[0.45] sm:text-lg"
          >
            {loading ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Confirmando...
              </>
            ) : (
              <>
                <FiCalendar className="h-5 w-5 flex-none" aria-hidden="true" />
                Confirmar agendamento
              </>
            )}
          </button>

          <p className="flex items-center justify-center gap-2 text-sm text-slate-500">
            <FiShield className="h-4 w-4" aria-hidden="true" /> Seus dados estão seguros
            conosco.
          </p>
        </form>
      </section>
    );
  }

  return (
    <section
      id="booking-form"
      className="animate-fade-in rounded-3xl border border-slate-200/80 bg-white p-5 shadow-card shadow-slate-200/70 transition-shadow hover:shadow-card-hover sm:p-6"
    >
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-primary-600" />
            <h2 className="text-xl font-bold tracking-tight text-slate-950 sm:text-2xl">
              Seus dados
            </h2>
          </div>
          <p className="mt-2 text-sm text-slate-500">
            Informe seus contatos para receber a confirmação do agendamento.
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        {isLoggedCustomer && (
          <div className="rounded-2xl border border-primary-200 bg-primary-50 px-3 py-2.5 text-xs font-semibold text-primary-800">
            {loggedCustomerNotice}
          </div>
        )}

        <Input
          label="Nome completo"
          icon={<FiUser />}
          value={formData.name}
          onChange={(e) => onFormDataChange({ name: e.target.value })}
          disabled={isLocked("name")}
          required
          placeholder="Digite seu nome"
        />

        <Input
          label="WhatsApp"
          icon={<FiMessageSquare />}
          value={formData.whatsapp}
          onChange={(e) => {
            const numbers = e.target.value.replace(/\D/g, "");
            let formatted = numbers;
            if (numbers.length >= 11) {
              formatted = numbers.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
            } else if (numbers.length >= 7) {
              formatted = numbers.replace(/(\d{2})(\d{5})(\d*)/, "($1) $2-$3");
            } else if (numbers.length >= 3) {
              formatted = numbers.replace(/(\d{2})(\d*)/, "($1) $2");
            }
            onFormDataChange({ whatsapp: formatted });
          }}
          disabled={isLocked("whatsapp")}
          required
          placeholder="(85) 99999-9999"
          maxLength={15}
          helperText="Número para confirmações e lembretes"
        />

        <Input
          label="E-mail para confirmação"
          icon={<FiMail />}
          type="email"
          value={formData.email}
          onChange={(e) => onFormDataChange({ email: e.target.value })}
          disabled={isLocked("email")}
          required
          placeholder="seu@email.com"
          error={emailIsInvalid ? "Informe um e-mail válido." : undefined}
          helperText="Usado para enviar a confirmação do agendamento."
        />

        <Input
          label="Telefone"
          icon={<FiPhone />}
          value={formData.phone}
          onChange={(e) => onFormDataChange({ phone: e.target.value })}
          placeholder="(11) 1234-5678"
        />

        <Textarea
          label="Observações"
          icon={<FiFileText />}
          value={formData.notes}
          onChange={(e) => onFormDataChange({ notes: e.target.value })}
          rows={3}
          placeholder="Alguma informação adicional que queira compartilhar"
        />

        {customFields.length > 0 && (
          <CustomFieldsForm
            fields={customFields}
            values={customValues}
            onChange={onCustomValuesChange}
          />
        )}

        {selectedTime && (
          <div className="mt-6 rounded-2xl border border-primary-200/70 bg-gradient-to-br from-primary-50 via-white to-secondary-50/60 p-5 shadow-sm">
            <div className="flex items-start gap-4">
              <span className="inline-flex rounded-2xl bg-primary-100 p-3 text-xl text-primary-700 shadow-sm">
                <FiCalendar aria-hidden="true" />
              </span>
              <div className="flex-1">
                <p className="mb-1.5 text-base font-bold text-primary-950">
                  Horário selecionado
                </p>
                <p className="text-sm font-semibold text-primary-800">
                  {new Date(date + "T00:00:00").toLocaleDateString("pt-BR", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}{" "}
                  às <span className="font-bold">{selectedTime}</span>
                  {selectedProviderId && providers.length > 0 && (
                    <>
                      <br />
                      <span className="text-sm text-primary-700">
                        Profissional:{" "}
                        <span className="font-semibold">
                          {providers.find((p) => p.id === selectedProviderId)?.name || ""}
                        </span>
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="pt-4">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={
              !hasSelectedService || !selectedTime || !contactIsComplete || loading
            }
            isLoading={loading}
            leftIcon={selectedTime ? <FiCheckCircle /> : <FiCalendar />}
          >
            {hasSelectedService
              ? selectedTime
                ? "Confirmar agendamento"
                : "Selecione um horário primeiro"
              : "Selecione um serviço"}
          </Button>
        </div>
      </form>
    </section>
  );
}
